import { useState, type FormEvent } from "react";
import { useLogout, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { api, rememberWorkspace, type Me } from "../providers/api/client";
import { CenteredCard } from "./CenteredCard";

/**
 * Signed in, but in no workspace (ADR-0007 §4). The instance's first user (or anyone, when creation
 * is open) creates one and becomes its owner; everyone else waits for an invitation link.
 */
export const NoWorkspacePage = ({ me }: { me: Me }) => {
  const translate = useTranslate();
  const logout = useLogout();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const workspace = await api<{ id: string }>("/workspaces", {
        method: "POST",
        body: { name: name.trim() },
      });
      rememberWorkspace(workspace.id);
      window.location.assign("/");
    } catch (failure) {
      setError((failure as Error).message);
      setPending(false);
    }
  };

  return (
    <CenteredCard
      title={translate("crm.workspaces.none.title", {
        _: "Welcome to Atomic CRM",
      })}
    >
      <p className="text-sm text-muted-foreground">
        {translate("crm.workspaces.none.signed_in_as", {
          _: "Signed in as %{email}.",
          email: me.user.email,
        })}
      </p>
      {me.can_create_workspace ? (
        <form className="space-y-4" onSubmit={create}>
          <div className="space-y-2">
            <Label htmlFor="workspace-name">
              {translate("crm.workspaces.none.name", {
                _: "Name your workspace",
              })}
            </Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Nashville Sessions"
              maxLength={120}
              required
              autoFocus
            />
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button
            type="submit"
            className="w-full"
            disabled={pending || !name.trim()}
          >
            {translate("crm.workspaces.none.create", {
              _: "Create workspace",
            })}
          </Button>
        </form>
      ) : (
        <p className="text-sm">
          {translate("crm.workspaces.none.ask", {
            _: "You're not in a workspace yet. Ask an owner or admin for an invitation link, then open it while signed in.",
          })}
        </p>
      )}
      <Button variant="outline" className="w-full" onClick={() => logout()}>
        {translate("ra.auth.logout", { _: "Log out" })}
      </Button>
    </CenteredCard>
  );
};
